import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { createImportJob, confirmImport, ImportPreviewRow } from './api';
import './inventory-v2.css';
import DataTable from '../../shared/components/DataTable';

const DEFAULT_MAPPING = {
  name: '',
  brand: '',
  supplier: '',
  unit: '',
  purchase_quantity: '',
  purchase_cost: '',
};

/**
 * Auto-detect column mapping from CSV/Excel headers.
 * Uses priority rules so that exact matches beat partial matches,
 * and canonical_ prefixed columns beat pack_ prefixed ones.
 */
function autoDetectMapping(headers: string[]) {
  const m = { ...DEFAULT_MAPPING };

  const pick = (tests: ((h: string) => boolean)[]) => {
    for (const test of tests) {
      const found = headers.find(h => test(h.toLowerCase()));
      if (found) return found;
    }
    return '';
  };

  m.name = pick([
    h => h === 'name',
    h => h === 'ingredient',
    h => h === 'item name',
    h => h.startsWith('name') || h.endsWith('name'),
    h => h.includes('ingredient') || h.includes('item'),
  ]);

  // canonical_unit beats pack_supplier_unit (numeric conversion factor)
  m.unit = pick([
    h => h === 'unit',
    h => h === 'canonical_unit',
    h => h === 'base_unit',
    h => h === 'uom',
    h => h.startsWith('canonical') && h.includes('unit'),
    // avoid pack_supplier_unit (it's numeric)
  ]);

  m.supplier = pick([
    h => h === 'supplier',
    h => h === 'supplier_name',
    h => h === 'vendor',
    h => h.startsWith('supplier'),
    h => h.includes('supplier'),
  ]);

  m.brand = m.supplier || pick([
    h => h === 'brand',
    h => h.includes('brand'),
  ]);

  // pack_supplier_quantity is the purchase qty (how many base units per pack)
  m.purchase_quantity = pick([
    h => h === 'purchase_quantity',
    h => h === 'purchase qty',
    h => h === 'qty',
    h => h === 'pack_supplier_quantity',
    h => h.includes('purchase') && h.includes('qty'),
    h => h.includes('purchase') && h.includes('quantity'),
    h => h.includes('quantity'),
  ]);

  // pack_cost is the purchase cost per pack
  m.purchase_cost = pick([
    h => h === 'purchase_cost',
    h => h === 'purchase cost',
    h => h === 'pack_cost',
    h => h.includes('purchase') && h.includes('cost'),
    h => h === 'cost',
    h => h.includes('cost'),
  ]);

  return m;
}

const ImportInventory: React.FC = () => {
  const { sessionToken } = useAuth();
  const [step, setStep] = useState<'upload' | 'mapping' | 'preview' | 'done'>('upload');
  const [rawData, setRawData] = useState<any[]>([]);
  const [preview, setPreview] = useState<ImportPreviewRow[]>([]);
  const [jobId, setJobId] = useState<number | null>(null);
  const [result, setResult] = useState<{ successful: number; failed: number } | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Column mapping state
  const [mapping, setMapping] = useState(DEFAULT_MAPPING);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Parse CSV/TSV (simplified — for Excel, user should export to CSV first)
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target?.result as string;
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) { setError('File appears empty'); return; }

      const headers = lines[0].split(/[,\t]/).map(h => h.trim().replace(/^"|"$/g, ''));
      const rows = lines.slice(1).map(line => {
        const cells = line.split(/[,\t]/).map(c => c.trim().replace(/^"|"$/g, ''));
        const row: any = {};
        headers.forEach((h, i) => { row[h] = cells[i] || ''; });
        return row;
      });

      setRawData(rows);
      setMapping(autoDetectMapping(headers));
      setStep('mapping');
    };
    reader.readAsText(file);
  };

  const handlePasteData = () => {
    const text = prompt('Paste your tab-separated data (with headers):');
    if (!text) return;

    const lines = text.split('\n').filter(l => l.trim());
    if (lines.length < 2) { setError('No data found'); return; }

    const headers = lines[0].split('\t').map(h => h.trim());
    const rows = lines.slice(1).map(line => {
      const cells = line.split('\t').map(c => c.trim());
      const row: any = {};
      headers.forEach((h, i) => { row[h] = cells[i] || ''; });
      return row;
    });

    setRawData(rows);
    setMapping(autoDetectMapping(headers));
    setStep('mapping');
  };

  const handlePreview = async () => {
    setError('');
    setLoading(true);
    try {
      const mappedRows = rawData.map(row => ({
        name: row[mapping.name] || '',
        brand: row[mapping.brand] || '',
        supplier: row[mapping.supplier] || '',
        unit: row[mapping.unit] || '',
        purchase_quantity: parseFloat(row[mapping.purchase_quantity]) || null,
        purchase_cost: parseFloat(row[mapping.purchase_cost]) || null,
      }));

      const res = await createImportJob(sessionToken, { source_type: 'excel', rows: mappedRows });
      setPreview(res.preview);
      setJobId(res.import_job.id);
      setStep('preview');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!jobId) return;
    setError('');
    setLoading(true);
    try {
      const res = await confirmImport(sessionToken, jobId);
      setResult({ successful: res.successful, failed: res.failed });
      setStep('done');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const availableColumns = rawData.length > 0 ? Object.keys(rawData[0]) : [];

  return (
    <div className="inv2-page">
      <div className="inv2-header">
        <div>
          <Link to="/inventory/items" className="inv2-back-link">← Back to Items</Link>
          <h1>📥 Import Inventory</h1>
        </div>
      </div>

      {error && <div className="inv2-error">{error}</div>}

      {/* Step 1: Upload */}
      {step === 'upload' && (
        <div className="inv2-section">
          <h2>Step 1: Upload Data</h2>
          <p>Upload a CSV/TSV file from your RawMaterials sheet, or paste data directly.</p>
          <div className="inv2-upload-options">
            <div className="inv2-upload-box">
              <label className="inv2-file-label">
                📄 Choose CSV/TSV File
                <input type="file" accept=".csv,.tsv,.txt" onChange={handleFileUpload} className="inv2-file-input" />
              </label>
            </div>
            <div className="inv2-upload-box">
              <button onClick={handlePasteData} className="inv2-btn">📋 Paste Tab-Separated Data</button>
            </div>
          </div>
          <div className="inv2-hint-box">
            <h4>Supported column names (auto-detected):</h4>
            <ul>
              <li><strong>Item Name:</strong> name, ingredient, item name</li>
              <li><strong>Unit:</strong> unit, canonical_unit, base_unit</li>
              <li><strong>Supplier:</strong> supplier, supplier_name, vendor</li>
              <li><strong>Purchase Qty:</strong> purchase_quantity, pack_supplier_quantity, qty</li>
              <li><strong>Purchase Cost:</strong> purchase_cost, pack_cost, cost</li>
            </ul>
            <p>You can adjust the mapping in Step 2 if auto-detection doesn't match your file.</p>
          </div>
        </div>
      )}

      {/* Step 2: Column Mapping */}
      {step === 'mapping' && (
        <div className="inv2-section">
          <h2>Step 2: Map Columns</h2>
          <p>Found {rawData.length} rows. Map your columns to the inventory fields:</p>
          <div className="inv2-form-grid">
            {Object.entries({ name: 'Item Name', brand: 'Brand', supplier: 'Supplier', unit: 'Unit', purchase_quantity: 'Purchase Quantity', purchase_cost: 'Purchase Cost' }).map(([key, label]) => (
              <div key={key} className="inv2-form-group">
                <label>{label}</label>
                <select value={(mapping as any)[key]} onChange={e => setMapping(p => ({ ...p, [key]: e.target.value }))} className="inv2-select">
                  <option value="">— Skip —</option>
                  {availableColumns.map(col => <option key={col} value={col}>{col}</option>)}
                </select>
              </div>
            ))}
          </div>
          <div className="inv2-form-actions">
            <button onClick={handlePreview} disabled={loading} className="inv2-btn inv2-btn-primary">
              {loading ? 'Processing...' : 'Preview Import'}
            </button>
            <button onClick={() => setStep('upload')} className="inv2-btn">Back</button>
          </div>
        </div>
      )}

      {/* Step 3: Preview */}
      {step === 'preview' && (
        <div className="inv2-section">
          <h2>Step 3: Review & Confirm</h2>
          <p>
            <strong>{preview.filter(r => !r.will_skip).length}</strong> items ready to import,
            <strong> {preview.filter(r => r.will_skip).length}</strong> will be skipped.
          </p>
          <div className="inv2-table-container">
            <DataTable
              rows={preview}
              rowKey={(row) => row.row_index}
              caption="Import preview"
              moreLabel="Costs"
              empty="Nothing to import"
              rowClass={(row) => (row.will_skip ? 'inv2-row-inactive' : undefined)}
              columns={[
                { key: 'name', header: 'Name', priority: 'primary', render: (row) => row.name || <span className="inv2-text-danger">Missing</span> },
                {
                  key: 'status', header: 'Status', priority: 'summary',
                  render: (row) => (
                    row.will_skip ? <span className="inv2-text-danger">Skip: {row.errors.join(', ')}</span>
                      : row.exists_in_db ? <span className="inv2-text-warning">Exists</span>
                      : <span className="inv2-text-success">✓ Ready</span>
                  ),
                },
                { key: 'qty', header: 'Qty', priority: 'summary', numeric: true, render: (row) => row.purchase_quantity ?? '—' },
                { key: 'unit', header: 'Unit', priority: 'summary', render: (row) => row.unit || <span className="inv2-text-danger">?</span> },
                { key: 'brand', header: 'Brand/Supplier', priority: 'summary', render: (row) => row.brand || row.supplier_name || '—' },
                { key: 'row', header: '#', priority: 'detail', numeric: true, render: (row) => row.row_index + 1 },
                { key: 'cost', header: 'Cost', priority: 'detail', numeric: true, render: (row) => (row.purchase_cost != null ? `$${Number(row.purchase_cost).toFixed(2)}` : '—') },
                { key: 'uprice', header: 'Unit Price', priority: 'detail', numeric: true, render: (row) => (row.unit_price != null ? `$${Number(row.unit_price).toFixed(4)}` : '—') },
              ]}
            />
          </div>
          <div className="inv2-form-actions">
            <button onClick={handleConfirm} disabled={loading} className="inv2-btn inv2-btn-primary">
              {loading ? 'Importing...' : 'Confirm Import'}
            </button>
            <button onClick={() => setStep('mapping')} className="inv2-btn">Back</button>
          </div>
        </div>
      )}

      {/* Step 4: Done */}
      {step === 'done' && result && (
        <div className="inv2-section">
          <h2>✅ Import Complete</h2>
          <div className="inv2-cards-grid">
            <div className="inv2-card inv2-card-success">
              <div className="inv2-card-value">{result.successful}</div>
              <div className="inv2-card-label">Items Imported</div>
            </div>
            <div className="inv2-card inv2-card-danger">
              <div className="inv2-card-value">{result.failed}</div>
              <div className="inv2-card-label">Failed/Skipped</div>
            </div>
          </div>
          <div className="inv2-form-actions">
            <Link to="/inventory/items" className="inv2-btn inv2-btn-primary">View Items</Link>
            <button onClick={() => { setStep('upload'); setRawData([]); setPreview([]); }} className="inv2-btn">Import More</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportInventory;
